"use client";
import Link from "next/link";
import { useEffect } from "react";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right";
import Filter from "lucide-react/dist/esm/icons/filter";
import Package from "lucide-react/dist/esm/icons/package";
import Search from "lucide-react/dist/esm/icons/search";
import Sparkles from "lucide-react/dist/esm/icons/sparkles";
import { Button } from "@/components/ui/button";
import { EmptySearchDemandCard } from "@/components/demand/EmptySearchDemandCard";
import { track } from "@/lib/analytics";

export interface RestrictiveFilterHint {
  label: string;
  description: string;
  onRemove: () => void;
}

interface BrowseEmptyStateProps {
  hasFilters: boolean;
  onClearFilters: () => void;
  /** Two most restrictive filters — per ascension plan empty state */
  restrictiveHints?: RestrictiveFilterHint[];
  /** The search that came up empty, for the "tell us what you want" card. */
  query?: string;
  categorySlug?: string | null;
}

export function BrowseEmptyState({
  hasFilters,
  onClearFilters,
  restrictiveHints,
  query,
  categorySlug,
}: BrowseEmptyStateProps) {
  useEffect(() => {
    track({ event: "browse_empty_state_shown", filter_count: restrictiveHints?.length ?? 0 });
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-20 px-4 text-center rounded-2xl border border-dashed border-border/50 bg-card/30">
      <div className="mb-8">
        <div className="relative">
          <div className="absolute -top-2 -left-2 rounded-xl bg-muted/50 border border-border/30 p-3 rotate-[-8deg] opacity-60">
            {hasFilters ? <Filter className="h-6 w-6 text-muted-foreground" /> : <Search className="h-6 w-6 text-muted-foreground" />}
          </div>
          <div className="relative rounded-2xl bg-card border border-border/50 p-5 shadow-sm">
            <Package className="h-12 w-12 text-primary" />
          </div>
        </div>
      </div>

      <h3 className="font-display text-2xl font-bold mb-3">{hasFilters ? "No matching assets" : "No assets yet"}</h3>

      <p className="text-muted-foreground max-w-md mb-8 leading-relaxed">
        {hasFilters
          ? "We couldn't find any assets matching your current filters. Try relaxing the most restrictive filters below."
          : "This category doesn't have any listings yet. If you build UEFN assets, you could be the first to sell here."}
      </p>

      {hasFilters && restrictiveHints && restrictiveHints.length > 0 && (
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 justify-center mb-8 w-full max-w-lg">
          {restrictiveHints.map((h) => (
            <div
              key={h.label + h.description}
              className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2 text-sm"
            >
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{h.label}</span>
              <span className="text-foreground/90">{h.description}</span>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs ml-auto" onClick={h.onRemove}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        {hasFilters ? (
          <>
            <Button variant="outline" onClick={onClearFilters} className="border-border/50 hover:border-primary/50 hover:bg-primary/5">
              Clear all filters
            </Button>
            <Button asChild variant="ghost" className="group">
              <Link href="/browse">
                Browse all assets
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </>
        ) : (
          <>
            <Button asChild>
              <Link href="/seller/onboarding">
                <Sparkles className="mr-2 h-4 w-4" />
                Start Selling
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-border/50 hover:border-primary/50 hover:bg-primary/5">
              <Link href="/">Explore Homepage</Link>
            </Button>
          </>
        )}
      </div>

      <EmptySearchDemandCard query={query} categorySlug={categorySlug} />
    </div>
  );
}
